/**
 * Versioned authorization-agreement content.
 *
 * Each entry is a self-contained snapshot of the agreement text/scope as it
 * existed at a point in time. NEVER edit an existing version's content in a way
 * that changes its legal meaning — if the wording, scope, or duration changes,
 * add a NEW version so we always know exactly what a given user signed.
 *
 * Both the on-screen document and the generated PDF render from this single
 * source, so the two can never drift apart.
 *
 * Body paragraphs are functions of a small context object so we can inject the
 * signer's name/address and the law list. Wrap any span in **double asterisks**
 * to mark it bold — the on-screen renderer turns those into <strong>, and the
 * PDF renderer strips the markers (jsPDF has no inline styling here).
 */

const STANDARD_PRIVACY_LAWS = [
  "CCPA/CPRA (California)",
  "VCDPA (Virginia)",
  "CPA (Colorado)",
  "CTDPA (Connecticut)",
  "UCPA (Utah)",
  "TDPSA (Texas)",
  "FDBR (Florida)",
  "MCDPA (Montana)",
  "OCPA (Oregon)",
  "TIPA (Tennessee)",
  "IDPL (Indiana)",
  "ICDPA (Iowa)",
  "DPDPA (Delaware)",
  "New Hampshire Privacy Act",
  "New Jersey Privacy Act",
  "Nebraska Data Privacy Act",
  "Maryland Online Data Privacy Act",
  "Minnesota Consumer Data Privacy Act",
  "Rhode Island Data Transparency Act",
  "Quebec Law 25 (Canada)",
];

/**
 * v1.0 — Ongoing authorization. Scope covers all consumer privacy rights
 * (know/access, correct, delete, withdraw consent, opt-out of sale/sharing/
 * targeted advertising, limit sensitive info). Authority is continuing and
 * survives until revoked, explicitly covering repeated/ongoing requests to
 * multiple recipients.
 */
const V1_0 = {
  version: "1.0",
  effectiveDate: "2026-07-29",
  title: "Authorization Agreement",
  privacyLaws: STANDARD_PRIVACY_LAWS,
  paragraphs: ({ fullName, formattedAddress, privacyLaws }) => [
    `I, **${fullName || "[Your Name]"}**, residing at **${
      formattedAddress || "[Your Address]"
    }**, hereby appoint **Kloudscan Inc** to act as my authorized agent in exercising my rights under applicable privacy and data protection laws.`,

    `This authorization applies to all applicable privacy legislation, including but not limited to: ${privacyLaws
      .map((law) => `**${law}**`)
      .join(", ")}.`,

    `Under this authorization, **Kloudscan Inc** is permitted to exercise, on my behalf, any and all consumer privacy rights available to me under applicable law, including but not limited to: the right to know and access the personal data held about me; the right to request correction of inaccurate personal data; the right to request deletion or erasure of my personal data; the right to withdraw consent to the processing of my personal data; the right to opt out of, and object to, the sale, sharing, or use of my personal data for targeted advertising; and the right to limit the use and disclosure of my sensitive personal information.`,

    `This authorization is ongoing and remains in effect until I revoke it. It authorizes **Kloudscan Inc** to submit and re-submit requests on my behalf on a continuing basis, including repeated requests to the same recipient and requests to additional recipients, whenever my personal data reappears, is re-collected, or is processed again, and is not limited to a single request or a single recipient. I understand that **Kloudscan Inc** may cease acting as my authorized agent at any time, and that I retain the right to revoke this authorization at any time by providing written notice.`,
  ],
};

/** Registry of every published version, keyed by version string. */
const AUTHORIZATION_VERSIONS = {
  [V1_0.version]: V1_0,
};

/** The version new signers are presented with. Bump when a newer version ships. */
export const LATEST_AUTHORIZATION_VERSION = V1_0.version;

/**
 * Resolve an agreement by version string. Falls back to the latest version if
 * the requested version is missing or unknown (e.g. legacy records with no
 * stored version), so the UI always has something coherent to render.
 */
export function getAuthorizationAgreement(version) {
  return (
    AUTHORIZATION_VERSIONS[version] ||
    AUTHORIZATION_VERSIONS[LATEST_AUTHORIZATION_VERSION]
  );
}

/**
 * Split a paragraph on **bold** markers. Returns an array of
 * `{ text, bold }` segments for renderers to map over.
 */
export function parseRichText(text) {
  return text
    .split(/(\*\*[^*]+\*\*)/g)
    .filter((segment) => segment !== "")
    .map((segment) =>
      segment.startsWith("**") && segment.endsWith("**")
        ? { text: segment.slice(2, -2), bold: true }
        : { text: segment, bold: false },
    );
}

/** Strip **bold** markers to plain text (used by the PDF renderer). */
export function stripRichText(text) {
  return text.replace(/\*\*/g, "");
}
